import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["badge", "frame"]
  static values = { unreadCountUrl: String, listUrl: String }

  connect() {
    this.loaded = false
    this.poll = setInterval(() => this.refreshUnreadCount(), 30000)

    this.onServiceWorkerMessage = (event) => {
      if (event.data?.type === "mark-notification-read") this.markAsRead(event.data.id)
      if (event.data?.type === "refresh-unread-count") this.refreshUnreadCount()
    }
    navigator.serviceWorker?.addEventListener("message", this.onServiceWorkerMessage)

    // Foreground pushes are rendered by push_notifications_controller.js
    // directly (they never reach the service worker), so it reports arrivals
    // this way instead of the postMessage channel above.
    this.onPushReceived = () => this.refreshUnreadCount()
    window.addEventListener("push:received", this.onPushReceived)

    this.markFromQueryParam()
  }

  disconnect() {
    clearInterval(this.poll)
    navigator.serviceWorker?.removeEventListener("message", this.onServiceWorkerMessage)
    window.removeEventListener("push:received", this.onPushReceived)
  }

  // A push notification opened in a fresh tab carries the notification id in
  // the URL (see service-worker.js.erb) since there's no page listening for
  // postMessage yet at that point.
  markFromQueryParam() {
    const url = new URL(window.location.href)
    const id = url.searchParams.get("read_notification")
    if (!id) return

    this.markAsRead(id)
    url.searchParams.delete("read_notification")
    window.history.replaceState({}, "", url)
  }

  markAsRead(id) {
    fetch(`/notifications/${id}/mark_as_read`, {
      method: "PATCH",
      headers: { "X-CSRF-Token": this.csrfToken }
    })
      .then(() => this.refreshUnreadCount())
      .catch(() => {})
  }

  get csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content
  }

  toggle() {
    if (this.loaded || !this.hasFrameTarget) return

    this.loaded = true
    this.frameTarget.src = this.listUrlValue
  }

  refreshUnreadCount() {
    fetch(this.unreadCountUrlValue, { headers: { Accept: "application/json" } })
      .then((response) => response.json())
      .then(({ count }) => this.updateBadge(count))
      .catch(() => {})
  }

  updateBadge(count) {
    if (!this.hasBadgeTarget) return

    const changed = this.badgeTarget.textContent.trim() !== String(count)
    this.badgeTarget.textContent = count
    this.badgeTarget.classList.toggle("hidden", count === 0)

    if (changed) this.loaded = false

    if (changed && count > 0) {
      this.badgeTarget.classList.add("animate-pulse")
      setTimeout(() => this.badgeTarget.classList.remove("animate-pulse"), 1000)
    }
  }
}
